/**
 * Phase E — small modal for creating a new workspace from the sidebar
 * switcher. Calls POST /api/workspaces, switches the active workspace on
 * success, and returns to /dashboard so all queries refetch under the new
 * scope.
 */

import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Briefcase } from 'lucide-react';
import axios from 'axios';
import { workspaceApi } from '@/lib/api';
import { useWorkspaceContext } from '@/components/WorkspaceProvider';
import { Button } from '@/components/ui/Button';
import {
  ModalOverlay,
  ModalDialog,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ErrorBox,
} from '@/components/posts/ModalPrimitives';

export function CreateWorkspaceModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setActiveWorkspaceId } = useWorkspaceContext();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => workspaceApi.create({ name: name.trim() }),
    onSuccess: (workspace) => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      setActiveWorkspaceId(workspace.id);
      onClose();
      navigate('/dashboard');
    },
    onError: (err) => {
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const backendMsg = (err.response?.data as { message?: string } | undefined)?.message;
        if (status === 402) {
          setError(backendMsg ?? t('errors.planLimit'));
          return;
        }
        setError(backendMsg ?? t('errors.createWorkspaceFailed', { status: status ?? '?' }));
        return;
      }
      setError(t('errors.createWorkspaceGeneric'));
    },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError(t('workspace.nameRequired'));
      return;
    }
    mutation.mutate();
  };

  return (
    <ModalOverlay onClose={onClose}>
      <ModalDialog maxWidthClass="max-w-md">
        <ModalHeader
          icon={<Briefcase className="h-4 w-4" />}
          title={t('workspace.createTitle')}
          subtitle={t('workspace.createSubtitle')}
          onClose={onClose}
        />
        <form onSubmit={handleSubmit}>
          <ModalBody>
            <div className="space-y-5">
              <div>
                <label className="field-label" htmlFor="workspace-name">
                  {t('workspace.nameLabel')}
                </label>
                <input
                  id="workspace-name"
                  type="text"
                  autoFocus
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('workspace.namePlaceholder')}
                  className="field-input"
                />
                <p className="text-text-tertiary mt-2 text-[11px]">{t('workspace.createNote')}</p>
              </div>
              {error && <ErrorBox message={error} />}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? t('workspace.creating') : t('workspace.createButton')}
            </Button>
          </ModalFooter>
        </form>
      </ModalDialog>
    </ModalOverlay>
  );
}
